import React, { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Divider, Form, Input } from "antd"
import { faBuilding, faEnvelope } from "@fortawesome/free-solid-svg-icons"
import { useRecoilState, useRecoilValue } from "recoil"
import { isAuthAtom, userAtom } from "@/atoms/userAtom"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { useTranslation } from "react-i18next"
import FormPhoneInput from "@/UI/Inputs/PhoneInput/FormPhoneInput"
import { SuccessNotification } from "@/UI/Notifications/NotificationTemplate.component"
import { IRoles } from "@/constants/role"
import { patchOrg } from "@/api/orgs"
import { countries } from "country-data"
import { getUserProfile } from "@/api/users"
import { IUser } from "@/types/user.types"
import SettingSection from "@/modules/shared/SettingSection"
import SettingContentWrapper from "@/modules/shared/SettingContentWrapper"
import DistanceUnitSwitcher from "@/modules/shared/Localization/DistanceUnitSwitcher"

interface OrganizationDetailsProps {}

const OrganizationDetails: React.FC<OrganizationDetailsProps> = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [fieldsChange, setFieldsChange] = useState<boolean>(false)
  const [user, setUser] = useRecoilState(userAtom)
  const isAuth = useRecoilValue(isAuthAtom)

  // Refresh profile on org switch
  useEffect(() => {
    resetFields()
  }, [user?.org])

  const refreshUser = () => {
    getUserProfile()
      .then(({ data }: { data: IUser }) => {
        setUser((userState) => ({
          ...userState,
          ...data,
          stores: data.stores,
          userFetched: true,
        }))
      })

      .catch((error: any) => {
        console.log("Get User Error:", error.message)
      })
  }

  const resetFields = () => {
    if (isAuth) {
      form.setFieldsValue({
        phone: user.org.phone,
        name: user.org.name,
        email: user.org.email,
      })
      setFieldsChange(false)
    }
  }

  const updateOrgInfo = (values: any) => {
    const payload = {
      name: values.name,
      phone: values.phone.replace(/\s/g, ""),
      email: values.email,
    }
    patchOrg(payload)
      .then(() => {
        SuccessNotification("Info Update", "Info updated successfully!")
        refreshUser()
      })
      .catch((error: any) => {
        console.log(error)
      })
  }

  useEffect(() => {
    resetFields()
  }, [])

  const isEditable = user.role === IRoles.OWNER

  return (
    <SettingSection title={t("profile.common.organization")}>
      <SettingContentWrapper>
        <div className="flex flex-col gap-x-2 mb-4">
          <div className="flex-row">
            <img
              src={`https://cdn.kcak11.com/CountryFlags/countries/${user.org.country.toLowerCase()}.svg`}
              width={32}
              height={32}
            />
            <span className="text-md font-semi ml-2">
              {countries[user.org.country.toUpperCase()]?.name}
            </span>
          </div>
          <div className="flex-row">
            <DistanceUnitSwitcher />
          </div>
        </div>
        <Form
          form={form}
          name="change-details"
          layout="vertical"
          id="general-settings-form"
          onFinish={updateOrgInfo}
          onValuesChange={() => {
            setFieldsChange(true)
          }}
          disabled={!isEditable}
        >
          <Form.Item
            name={"name"}
            validateTrigger="onSubmit"
            label={t("common.forms.businessName")}
            className="mb-4"
          >
            <Input
              prefix={
                <FontAwesomeIcon
                  className="flex-shrink-0 h-4 text-gray-400 ml-1 mr-2"
                  icon={faBuilding}
                />
              }
              // Error label does not exist on type
              // @ts-ignore
              className="profile-input"
            />
          </Form.Item>
          <Form.Item
            name={"email"}
            validateTrigger="onSubmit"
            label={t("profile.GeneralSettings.emailAddress")}
            className="mb-4"
          >
            <Input
              prefix={
                <FontAwesomeIcon
                  className="flex-shrink-0 h-4 text-gray-400 ml-1 mr-2"
                  icon={faEnvelope}
                />
              }
              className="profile-input"
            />
          </Form.Item>
          <FormPhoneInput title={t("common.phoneNumber")} form={form} isDisabled={!isEditable} />
          {isEditable && (
            <>
              <Divider className="mt-6 mb-4" />
              <div className="flex flex-row justify-end">
                <KosmoButton
                  id="general-settings-cancel-btn"
                  onClick={() => resetFields()}
                  isDisabled={!fieldsChange}
                  classname="mr-3"
                >
                  {t("common.cancel")}
                </KosmoButton>
                <KosmoButton
                  id="general-settings-save-btn"
                  type="primary"
                  htmlType="submit"
                  isDisabled={!fieldsChange}
                >
                  {t("profile.GeneralSettings.generalSettings.saveChanges")}
                </KosmoButton>
              </div>
            </>
          )}
        </Form>
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default OrganizationDetails
